import React, { useState, useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";

const LeftToRightRobotAdd = () => {
  const [number1, setNumber1] = useState(0);
  const [number2, setNumber2] = useState(0);
  const [step, setStep] = useState(0);
  const [tensSum, setTensSum] = useState(0);
  const [unitsSum, setUnitsSum] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [finalAnswer, setFinalAnswer] = useState('');
  const [instruction, setInstruction] = useState('');
  const [feedback, setFeedback] = useState('');
  const [showNext, setShowNext] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [showFinalInput, setShowFinalInput] = useState(false);

  const inputRef = useRef(null);

  // ✅ Helper: make robot speak
  const robotSpeak = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find(v => v.name.includes("Google UK English Male")) || voices[0];
    utterance.pitch = 0.6;
    utterance.rate = 1.2;
    utterance.volume = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const styles = {
    body: {
      fontFamily: "'Fredoka One', cursive, sans-serif",
      background: '#e0f7fa',
      color: '#006064',
      padding: '2rem 1rem',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    container: {
      background: 'white',
      borderRadius: '1rem',
      padding: '2rem',
      width: '100%',
      maxWidth: '480px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
      textAlign: 'center',
    },
    h1: { fontSize: '2rem', marginBottom: '1rem', color: '#00796b' },
    problem: { fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem' },
    instruction: { fontSize: '1.2rem', marginBottom: '1.5rem', minHeight: '48px' },
    input: {
      fontSize: '1.4rem',
      padding: '0.5rem 1rem',
      border: '3px solid #00796b',
      borderRadius: '0.75rem',
      textAlign: 'center',
      width: '100%',
      maxWidth: '180px',
      margin: '0 auto 1rem',
      display: 'block',
    },
    button: {
      fontSize: '1rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: '#00796b',
      color: 'white',
      border: 'none',
      borderRadius: '2rem',
      fontWeight: 700,
      cursor: 'pointer',
      margin: '0.5rem auto',
      display: 'block',
      width: 'fit-content',
    },
    feedback: { fontSize: '1.2rem', fontWeight: 700, color: '#004d40', minHeight: '40px', marginTop: '1.5rem' },
  };

  const generateProblem = () => {
    let t1 = Math.floor(Math.random() * 10) * 10;
    let t2 = Math.floor(Math.random() * 10) * 10;
    let u1 = Math.floor(Math.random() * 10);
    let u2 = Math.floor(Math.random() * 10);
    while (u1 + u2 >= 10) {
      u1 = Math.floor(Math.random() * 10);
      u2 = Math.floor(Math.random() * 10);
    }

    const n1 = t1 + u1;
    const n2 = t2 + u2;

    setNumber1(n1);
    setNumber2(n2);
    setTensSum(t1 + t2);
    setUnitsSum(u1 + u2);
    setStep(0);
    setInputValue('');
    setFinalAnswer('');
    setShowNext(false);
    setShowFinalInput(false);
    setFeedback('');
    const firstInstruction = `Robot says: Start with tens. ${t1} plus ${t2} equals what?`;
    setInstruction(`🤖 ${firstInstruction}`);
    setInputDisabled(false);
    setTimeout(() => inputRef.current?.focus(), 100);

    robotSpeak(firstInstruction);
  };

  useEffect(() => {
    generateProblem();
  }, []);

  const handleSubmit = () => {
    const answer = Number(inputValue.trim());
    if (step === 0) {
      if (answer === tensSum) {
        setFeedback("✅ Correct! Tens added correctly.");
        setStep(1);
        const nextInstruction = `Now add the units. ${number1 % 10} plus ${number2 % 10} equals what?`;
        setInstruction(`🤖 ${nextInstruction}`);
        setInputValue('');
        robotSpeak(nextInstruction);
      } else {
        const msg = "Oops! Try adding the tens again.";
        setFeedback("❌ " + msg);
        setInputValue('');
        robotSpeak(msg);
      }
    } else if (step === 1) {
      if (answer === unitsSum) {
        setFeedback("✅ Units added correctly!");
        setShowFinalInput(true);
        setInstruction(`🤖 Now, enter the total sum of ${number1} + ${number2}:`);
        setInputDisabled(true);
        setStep(2);
        robotSpeak(`Great! Now, enter the total sum of ${number1} plus ${number2}`);
      } else {
        const msg = "Not quite right, try adding the units again.";
        setFeedback("❌ " + msg);
        setInputValue('');
        robotSpeak(msg);
      }
    }
  };

  const handleFinalSubmit = () => {
    const answer = Number(finalAnswer.trim());
    const total = number1 + number2;
    if (answer === total) {
      const msg = `🎉 Correct! The total is ${total}. Well done!`;
      setFeedback(msg);
      setShowNext(true);
      setShowFinalInput(false);
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
      robotSpeak(msg);
    } else {
      const msg = "❌ Incorrect, try again!";
      setFeedback(msg);
      setFinalAnswer('');
      robotSpeak(msg);
    }
  };

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <h1 style={styles.h1}>🤖 Left-to-Right Robot Add</h1>
        <div style={styles.problem}>{`${number1} + ${number2} = ?`}</div>
        <div style={styles.instruction}>{instruction}</div>

        {!showFinalInput && (
          <>
            <input
              type="number"
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={inputDisabled}
              style={styles.input}
            />
            <button
              onClick={handleSubmit}
              disabled={inputValue.trim() === '' || inputDisabled}
              style={styles.button}
            >
              Submit
            </button>
          </>
        )}

        {showFinalInput && (
          <>
            <input
              type="number"
              value={finalAnswer}
              onChange={(e) => setFinalAnswer(e.target.value)}
              style={styles.input}
              placeholder="Enter total"
            />
            <button onClick={handleFinalSubmit} style={styles.button}>
              Submit Total
            </button>
          </>
        )}

        {showNext && (
          <button style={styles.button} onClick={generateProblem}>
            Next Problem
          </button>
        )}

        <div style={styles.feedback}>{feedback}</div>
      </div>
    </div>
  );
};

export default LeftToRightRobotAdd;
